// Admin → Performance Data tab.
// Two dropzones: Coil Performance, Airflow Data. Each card shows current
// row count + per-model breakdown, an "Upload" dropzone that REPLACES the
// dataset (POST), a "Download current" Excel export, and a "Download blank
// template" with example rows. Admin-only — backend gates everything via
// the role-check dependency.

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  Database,
  Wind,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Trigger a browser download from an axios { data, headers } response.
function triggerBlobDownload(blob, fallbackName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function ByModelList({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-muted-foreground italic">No rows in the database yet.</div>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {rows.map((r) => (
        <span
          key={r.model}
          className="inline-flex items-center gap-1 rounded border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
          data-testid={`perf-bymodel-${r.model.replace(/[^a-z0-9]+/gi, "-")}`}
        >
          <span>{r.model}</span>
          <span className="text-muted-foreground/70">·</span>
          <span className="tabular-nums">{r.rows}</span>
        </span>
      ))}
    </div>
  );
}

function DatasetCard({
  testid,
  icon: Icon,
  title,
  subtitle,
  importEndpoint,
  exportEndpoint,
  templateEndpoint,
  totalRows,
  byModel,
  onReload,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [hover, setHover] = useState(false);

  const upload = useCallback(async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls") && !lower.endsWith(".csv")) {
      toast.error("Please upload an .xlsx, .xls or .csv file.");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}${importEndpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.records_imported} row(s) imported. ${data.models?.length ?? 0} model(s) covered.`);
      onReload?.();
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [importEndpoint, onReload]);

  const download = useCallback(async (endpoint, fallbackName, key) => {
    setDownloading(key);
    try {
      const resp = await axios.get(`${API}${endpoint}`, { responseType: "blob" });
      triggerBlobDownload(resp.data, fallbackName);
    } catch (e) {
      toast.error(formatApiError(e, "Download failed."));
    } finally {
      setDownloading(null);
    }
  }, []);

  return (
    <Card data-testid={testid}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-emerald-700" />
          <span>{title}</span>
          <span
            data-testid={`${testid}-total`}
            className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 tabular-nums"
          >
            {totalRows} rows
          </span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Per-model breakdown */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Rows by model
          </div>
          <ByModelList rows={byModel} />
        </div>

        {/* Dropzone */}
        <div
          className={`mt-2 border-2 border-dashed rounded-lg px-4 py-6 text-center transition cursor-pointer ${
            hover ? "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20" : "border-border bg-muted/50"
          } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={(e) => {
            e.preventDefault();
            setHover(false);
            const f = e.dataTransfer.files?.[0];
            if (f) upload(f);
          }}
          onClick={() => fileRef.current?.click()}
          data-testid={`${testid}-dropzone`}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid={`${testid}-file-input`}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-emerald-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm font-medium">Uploading…</span>
            </div>
          ) : (
            <div className="text-foreground">
              <Upload className="h-6 w-6 mx-auto mb-1.5 text-muted-foreground" />
              <div className="text-sm font-medium">
                Drop .xlsx / .csv here, or click to choose
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Uploading <strong>replaces</strong> the entire dataset (transactional — failed parse rolls back).
              </div>
            </div>
          )}
        </div>

        {/* Action row */}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => download(exportEndpoint, fallbackName(testid, "current"), "export")}
            disabled={downloading !== null || totalRows === 0}
            data-testid={`${testid}-export`}
          >
            {downloading === "export"
              ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              : <Download className="h-4 w-4 mr-1.5" />}
            Download current
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => download(templateEndpoint, fallbackName(testid, "template"), "template")}
            disabled={downloading !== null}
            data-testid={`${testid}-template`}
          >
            {downloading === "template"
              ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}
            Blank template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function fallbackName(testid, kind) {
  const key = testid.replace(/^perf-/, "");
  return `zamba_${key}_${kind}.xlsx`;
}

export default function AdminPerformanceTab() {
  const [summary, setSummary] = useState({
    coils: { total_rows: 0, by_model: [] },
    airflow_data: { total_rows: 0, by_model: [] },
  });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data/summary`);
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load data summary."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4" data-testid="admin-performance-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Performance Data</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Upload coil-performance and airflow tables. Uploads replace the entire dataset for that table —
            engineers see the new numbers on their next selection run. Other reference documents
            (wiring, P&amp;ID, brochures, IOM, certifications) live on the <strong>Documents</strong> tab.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={reload}
          disabled={loading}
          data-testid="admin-performance-reload"
        >
          {loading
            ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            : <RefreshCw className="h-4 w-4 mr-1.5" />}
          Reload
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DatasetCard
          testid="perf-coils"
          icon={Database}
          title="Coil Performance"
          subtitle="One workbook with one sheet per model. Sheet name = model name. Capacities, water flow / PD, leaving water temp per length, airflow & circuits."
          importEndpoint="/admin/data/coils/import"
          exportEndpoint="/admin/data/coils/export"
          templateEndpoint="/admin/data/coils/template"
          totalRows={summary.coils?.total_rows ?? 0}
          byModel={summary.coils?.by_model ?? []}
          onReload={reload}
        />
        <DatasetCard
          testid="perf-airflow"
          icon={Wind}
          title="Airflow Data"
          subtitle="Primary / Secondary / Supply CFM + induction ratio per (model, length, speed)."
          importEndpoint="/admin/data/airflow/import"
          exportEndpoint="/admin/data/airflow/export"
          templateEndpoint="/admin/data/airflow/template"
          totalRows={summary.airflow_data?.total_rows ?? 0}
          byModel={summary.airflow_data?.by_model ?? []}
          onReload={reload}
        />
      </div>
    </div>
  );
}
